import { type ComponentProps } from 'react';
import { enableSyncFlagColors } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Diamond } from '@gonfalon/icons/Diamond';
import { type Variation, type WeightedVariation } from '@gonfalon/openapi/types';
import clsx from 'clsx';

import type { VariationColor } from '../../../ui/VariationColor';
import { getVariationFillColor } from '../../../utils/getVariationFillColor';

import styles from './VariationStack.module.css';

const defaultDiamondCount = 4;

type VariationUnion = WeightedVariation | Variation | { variationIndex: number };

export function VariationStack({
  variations,
  start = 0,
  limit = defaultDiamondCount,
  size = 'small',
  className,
}: {
  variations: VariationUnion[];
  start?: number;
  limit?: number;
  size?: ComponentProps<typeof VariationColor>['size'];
  className?: string;
}) {
  const isSyncFlagColorsEnabled = enableSyncFlagColors();
  const someVariations = variations.slice(start, start + limit);
  const remainingVariations = variations.slice(start + limit);

  const indices: number[] = [];
  for (const [index, variation] of someVariations.entries()) {
    if ('variationIndex' in variation) {
      indices.push(variation.variationIndex);
    } else if ('variation' in variation) {
      indices.push(variation.variation);
    } else {
      indices.push(index);
    }
  }
  const getVariationColor = (index: number, variation: VariationUnion | undefined) => {
    if (variation && isSyncFlagColorsEnabled && 'color' in variation && variation.color) {
      return variation.color;
    }
    return getVariationFillColor(index);
  };

  return (
    <div className={clsx(styles.colors, className)} data-test-id="variation-stack">
      {someVariations.map((variation, i) => (
        <Diamond
          key={`variation-${indices[i]}`}
          size={size}
          className={clsx(styles.color, size !== undefined && size !== null ? styles[size] : undefined)}
          stroke={variations.length > 1 && indices[i] > 0 ? 'var(--lp-color-bg-ui-primary)' : undefined}
          fill={getVariationColor(indices[i], variation)}
        />
      ))}
      {remainingVariations.length > 0 && start === 0 ? (
        <span className={styles.remaining}>+{remainingVariations.length}</span>
      ) : undefined}
      {start > 0 && <span>+ {start}</span>}
    </div>
  );
}
